// src/pages/PrivacyPage.js
//
// Route: /privacy
// Version: v0.1 — working draft, pre-lawyer review
// Last updated: June 2026
//
// GDPR-oriented privacy policy for EU/Spain-based product.
// Covers: what we collect, lawful basis, retention, rights, sub-processors.

import React from 'react'

const EFFECTIVE_DATE = 'June 2026'
const CONTACT_EMAIL  = 'hello@holasam.app'
const VERSION        = 'v0.1'

function Section({ title, id, children }) {
  return (
    <section className="legal-section" id={id}>
      <h2 className="legal-h2">{title}</h2>
      {children}
    </section>
  )
}

function Table({ headers, rows }) {
  return (
    <div className="legal-table-wrap">
      <table className="legal-table">
        <thead>
          <tr>{headers.map(h => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function PrivacyPage() {
  return (
    <div className="legal-root">
      <div className="legal-col">

        <header className="legal-header">
          <a href="/" className="legal-wordmark">Hola<span>Sam</span></a>
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-meta">Effective: {EFFECTIVE_DATE} · {VERSION}</p>
          <p className="legal-meta">
            This is a working draft. It will be reviewed by a qualified lawyer before
            HolaSam reaches significant public use.
          </p>
        </header>

        <Section title="Who we are" id="who">
          <p>
            HolaSam is an educational tax orientation tool for US persons living in
            Spain, operated from Spain. We are the data controller for personal data
            processed through this service.
          </p>
          <p>
            Contact: <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>
          </p>
        </Section>

        <Section title="What we collect and why" id="what">
          <p>
            We collect only what we need to provide the service. Here is what we
            collect, why, and on what legal basis under GDPR:
          </p>
          <Table
            headers={['Data', 'Why we collect it', 'Lawful basis', 'How long we keep it']}
            rows={[
              [
                'Email address — service use',
                'To send your sign-in link, deliver your snapshot and assessment, and allow you to access your account',
                'Contract / pre-contractual necessity',
                'Duration of account + 30 days after deletion',
              ],
              [
                'Email address — marketing',
                'To send occasional tax orientation content and HolaSam updates, only if you opt in',
                'Consent (separate opt-in checkbox — withdrawable at any time)',
                'Until you unsubscribe + 30 days',
              ],
              [
                'Tax profile data (residency, income sources, asset types, etc.)',
                'To generate your triage snapshot and full assessment',
                'Contract (necessary to provide the service)',
                'Duration of account + deletion on request',
              ],
              [
                'Chat history',
                'To provide context-aware responses in the assessment chat',
                'Contract',
                '12 months, then anonymised',
              ],
              [
                'Assessment outputs',
                'To display and persist your assessment results',
                'Contract (contains derived personal data)',
                'Same as chat history',
              ],
              [
                'Partner share authorisation',
                'To log your explicit consent when you share your snapshot with a named advisor',
                'Legal obligation (GDPR consent record)',
                'Indefinite — proof of authorisation',
              ],
              [
                'Stripe payment data',
                'To process one-time access pass purchases',
                'Contract + legal obligation',
                '7 years (Spanish accounting law)',
              ],
              [
                'Consent records',
                'To demonstrate lawful basis for processing and marketing',
                'Legal obligation',
                'Indefinite',
              ],
              [
                'IP address / device data',
                'Server-side security and fraud prevention',
                'Legitimate interest',
                '90 days',
              ],
              [
                'Cloudflare Analytics (cookieless)',
                'Aggregate site usage — no personal identifiers, no cookies, no cross-site tracking',
                'Legitimate interest (low-friction, privacy-preserving analytics)',
                'Cloudflare default (90 days aggregated)',
              ],
              [
                'Triage session data (unclaimed)',
                'Anonymous sessions that were not converted to an account',
                'Legitimate interest',
                '30 days, then deleted automatically',
              ],
            ]}
          />
        </Section>

        <Section title="AI processing" id="ai">
          <p>
            HolaSam uses Anthropic Claude to generate assessment outputs and chat
            responses. When you use these features, relevant parts of your tax profile
            are sent to Anthropic's API as part of the prompt.
          </p>
          <p>
            Anthropic processes this data as a sub-processor under our instructions.
            Anthropic's API terms do not permit training on API user data. We have
            documented this in our internal records of processing.
          </p>
          <p>
            We do not send more data to the AI than is needed to answer your question.
          </p>
        </Section>

        <Section title="Partner advisors" id="partners">
          <p>
            If you choose to share your snapshot with a partner advisor through
            HolaSam, we will tell you the name of that advisor before anything is
            shared. Sharing requires your explicit action — it is never automatic.
          </p>
          <p>
            Once shared, the advisor receives your triage answers and the contact
            details you provide. HolaSam logs every share with a timestamp.
          </p>
          <p>
            The advisor then acts as an independent data controller for their own
            follow-up. Their privacy practices are governed by their own policies.
          </p>
        </Section>

        <Section title="Sub-processors" id="subprocessors">
          <p>We use the following third-party services to operate HolaSam:</p>
          <Table
            headers={['Service', 'What they process', 'Location']}
            rows={[
              ['Supabase', 'Database, authentication, file storage', 'EU (AWS eu-west-1)'],
              ['Anthropic', 'AI prompt processing for assessments and chat', 'USA (with EU SCCs)'],
              ['Stripe', 'Payment processing', 'USA (with EU SCCs)'],
              ['Cloudflare', 'CDN, hosting, cookieless analytics', 'Global'],
            ]}
          />
        </Section>

        <Section title="Cookies" id="cookies">
          <p>
            HolaSam uses only strictly necessary cookies (authentication session
            management). We do not currently use advertising or analytics cookies.
          </p>
          <p>
            Our site analytics are provided by Cloudflare Analytics, which is
            cookieless and does not track you across sites.
          </p>
          <p>
            If we add analytics or marketing cookies in the future, we will ask for
            your consent before they are set, via a cookie consent banner.
          </p>
        </Section>

        <Section title="Your rights" id="rights">
          <p>
            Under GDPR, you have the right to:
          </p>
          <ul className="legal-list">
            <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
            <li><strong>Rectification</strong> — ask us to correct inaccurate data</li>
            <li><strong>Erasure</strong> — ask us to delete your data (subject to legal retention obligations)</li>
            <li><strong>Portability</strong> — receive your data in a structured, machine-readable format</li>
            <li><strong>Restriction</strong> — ask us to limit processing in certain circumstances</li>
            <li><strong>Objection</strong> — object to processing based on legitimate interest</li>
            <li><strong>Withdraw consent</strong> — unsubscribe from marketing emails at any time</li>
          </ul>
          <p>
            To exercise any of these rights, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
            We aim to respond within 30 days. You also have the right to lodge a
            complaint with the Spanish data protection authority (
            <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="legal-link">AEPD</a>).
          </p>
        </Section>

        <Section title="Data security" id="security">
          <p>
            User data is stored in Supabase with row-level security policies that
            ensure each user can only access their own data. Access to production
            data is restricted and logged.
          </p>
          <p>
            No system is perfectly secure. If you believe your data has been
            compromised, contact us immediately at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="Changes to this policy" id="changes">
          <p>
            We will update this policy as the product evolves. The version and
            effective date are shown at the top. If we make material changes that
            affect how we use your personal data, we will notify you by email
            if we have a valid address for you.
          </p>
        </Section>

        <footer className="legal-footer">
          <a href="/" className="legal-link">← Back to HolaSam</a>
          <span className="legal-footer-sep">·</span>
          <a href="/terms" className="legal-link">Terms of Service</a>
        </footer>

      </div>
    </div>
  )
}